import { useEffect, useState } from 'react';

import BpkProgress from '@skyscanner-internal/backpack-web/bpk-component-progress';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';

import { useItineraryVisibility } from '../../../App/ItineraryVisibilityContext';
import { useTranslation } from '../../helpers/hooks/useTranslation';
import { MAX_POLL_REQUESTS } from '../../network/hooks/itineraries/constants';

import { ItineraryCard, ItineraryCardLoading } from './ItineraryCard';
import './ItineraryCardContainer.scss';
import { useItineraryCardData } from './useItineraryCardData';

export const PROGRESS_HIDE_DELAY_MS = 500;

export const ItineraryCardContainer = () => {
  const {
    handleCardClick,
    handleShowDetails,
    isSessionActive,
    itineraryCardProps,
    pollCount,
    shouldShowItineraryCard,
    showCard,
    showLoading,
  } = useItineraryCardData();
  const translate = useTranslation();
  // Calculate progress: during active session show incremental progress, on complete show 100%
  const progressValue = isSessionActive
    ? Math.min((pollCount / MAX_POLL_REQUESTS) * 100, 95) // Cap at 95% while polling
    : 100;

  // Manage progress bar visibility with delayed hiding to show 100% briefly
  const [showProgressBar, setShowProgressBar] = useState(isSessionActive);

  useEffect(() => {
    if (isSessionActive) {
      setShowProgressBar(true);
      return undefined;
    }

    // Delay hiding to show 100% briefly before disappearing
    const timeout = setTimeout(
      () => setShowProgressBar(false),
      PROGRESS_HIDE_DELAY_MS,
    );
    return () => clearTimeout(timeout);
  }, [isSessionActive]);

  const isVisible = shouldShowItineraryCard && (showLoading || showCard);
  const { setItineraryLoading, setItineraryVisible } = useItineraryVisibility();

  useEffect(() => {
    setItineraryLoading(showProgressBar);
    setItineraryVisible(isVisible);
    return () => {
      setItineraryVisible(false);
    };
  }, [isVisible, showProgressBar, setItineraryVisible, setItineraryLoading]);

  // Double-check gate in case container is used standalone
  if (!shouldShowItineraryCard) {
    return null;
  }

  return (
    <div
      className={`month-view-ctas__itinerary-panel ${isVisible ? 'month-view-ctas__itinerary-panel--open' : ''}`}
    >
      <div className="month-view-ctas__itinerary-panel-inner">
        {(showLoading || showCard) && (
          <div className="month-view-ctas__cheapest-flight-header">
            <BpkText textStyle={TEXT_STYLES.label1}>
              {translate(
                'mv_cheapestDates_dealHunter_cheapest_available_flight_heading',
              )}
            </BpkText>
          </div>
        )}
        {showProgressBar && (
          <div className="month-view-ctas__progress-bar">
            <BpkProgress
              min={0}
              max={100}
              value={progressValue}
              small
              aria-label="Searching for prices"
            />
          </div>
        )}
        {showLoading && <ItineraryCardLoading />}
        {showCard && itineraryCardProps && (
          <ItineraryCard
            {...itineraryCardProps}
            onShowDetails={handleShowDetails}
            onCardClick={
              isSessionActive ? () => handleCardClick(false) : undefined
            }
            showButton={!showProgressBar}
          />
        )}
      </div>
    </div>
  );
};
